import { useEffect, useMemo, useState } from 'react';
import { useConfig } from './configContext';

interface WhiteLabelTexts {
  title: string;
  testimonial: {
    text: string;
    author: string;
  };
}

interface WhiteLabelTheme {
  primary?: string;
  'primary-foreground'?: string;
  secondary?: string;
  'sign-in'?: string;
}

export const useWhitelabel = () => {
  const { config } = useConfig();
  const [whitelabelTexts, setWhitelabelTexts] = useState<WhiteLabelTexts>({
    title: 'Maritime Domain Monitor',
    testimonial: {
      author: 'Gatehouse Customer',
      text: "This application has revolutionized our ship traffic management, streamlining operations and ensuring timely deliveries. It's saved us countless hours and significantly reduced logistical challenges, allowing us to serve our clients more efficiently than ever before.",
    },
  });
  const [whitelabelTheme, setWhitelabelTheme] = useState<WhiteLabelTheme>({});
  const [isLoadingTexts, setIsLoadingTexts] = useState(true);
  const [isLoadingTheme, setIsLoadingTheme] = useState(true);

  useEffect(() => {
    const fetchTexts = async () => {
      try {
        const response = await fetch(
          `/${config.whitelabel}/whitelabelTexts.json`
        );
        if (!response.ok) {
          throw new Error(
            `Failed to fetch whitelabelTexts.json: ${response.statusText}`
          );
        }
        const data = await response.json();
        setWhitelabelTexts(data);
      } catch (error) {
        console.error('Error fetching whitelabelTexts:', error);
      } finally {
        setIsLoadingTexts(false);
      }
    };

    fetchTexts();
  }, [config.whitelabel]);

  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const response = await fetch(
          `/${config.whitelabel}/whitelabelTheme.json`
        );
        if (!response.ok) {
          throw new Error(
            `Failed to fetch whiteLabelTheme.json: ${response.statusText}`
          );
        }
        const data = await response.json();
        setWhitelabelTheme(data);
      } catch (error) {
        console.error('Error fetching whitelabelTheme:', error);
      } finally {
        setIsLoadingTheme(false);
      }
    };

    fetchTheme();
  }, [config.whitelabel]);

  const whitelabelImagePaths = useMemo(() => {
    return {
      faviconPath: `/${config.whitelabel}/favicon.png`,
      sidebarCollapsedPath: `/${config.whitelabel}/sidebar_collapsed.png`,
      sidebarExpandedPath: `/${config.whitelabel}/sidebar_expanded.png`,
      signInPath: `/${config.whitelabel}/sign_in.png`,
    };
  }, [config.whitelabel]);

  const isLoading = useMemo(
    () => isLoadingTexts || isLoadingTheme,
    [isLoadingTexts, isLoadingTheme]
  );

  return {
    whitelabelImagePaths,
    whitelabelTexts,
    whitelabelTheme,
    isLoading,
    isLoadingTexts,
    isLoadingTheme,
  };
};
